import { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import { PrismicNextLink } from '@prismicio/next';
import { LinkField } from '@prismicio/client';
import * as prismicH from '@prismicio/helpers';

import { Link } from '@/components/atoms/link';
import { resolveLink } from '@/utils';

type SideNavLinkProps = {
  label: string;
  internalLink?: string | null;
  externalLink?: LinkField | null;
  className?: string;
  style?: CSSProperties;
  openExternalInNewTab?: boolean;
  preferExternal?: boolean;
  fallbackHref?: string;
  onNavigate?: MouseEventHandler<HTMLAnchorElement>;
  children?: ReactNode;
};

const SideNavLink = ({
  label,
  internalLink,
  externalLink,
  className,
  style,
  openExternalInNewTab = false,
  preferExternal = false,
  fallbackHref = '/',
  onNavigate,
  children,
}: SideNavLinkProps) => {
  const linkOptions = { externalNewTab: openExternalInNewTab };

  let resolved;
  if (preferExternal) {
    resolved = prismicH.isFilled.link(externalLink)
      ? resolveLink(null, externalLink, linkOptions)
      : resolveLink(internalLink, null, linkOptions);
  } else if (internalLink?.trim()) {
    resolved = resolveLink(internalLink, null, linkOptions);
  } else {
    resolved = resolveLink(null, externalLink, linkOptions);
  }

  const opensNewTab =
    'target' in resolved.props && resolved.props.target === '_blank';

  const accessibleName = label?.trim() || 'link';
  const ariaLabel = opensNewTab
    ? `Go to ${accessibleName} (opens in a new tab)`
    : `Go to ${accessibleName}`;

  if (resolved.kind === 'empty') {
    return (
      <Link
        aria-label={ariaLabel}
        className={className}
        href={fallbackHref}
        onClick={onNavigate}
        style={style}
      >
        {children ?? label}
      </Link>
    );
  }

  return (
    <PrismicNextLink
      {...resolved.props}
      aria-label={ariaLabel}
      className={className}
      onClick={onNavigate}
      style={style}
    >
      {children ?? label}
    </PrismicNextLink>
  );
};

export { SideNavLink };
export type { SideNavLinkProps };
